import ListViewModel from "viewModels/listViewModel";
import { action, computed, observable } from "mobx";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { Router } from "@frui.ts/screens";
import InternalReportsRepository from "repositories/internalReportsRepository";
import EventBus from "services/eventBus";
import SecurityService from "services/securityService";
import LocalizationService from "services/localizationService";
import NotificationService from "services/notificationService";
import InternalReportResponse from "entities/internalReportResponse";
import { watchBusy } from "utils/busyWatcher";
import { allowToDownload } from "../rights";
import { Modules } from "config";
import * as Sentry from "@sentry/react";
import download from "downloadjs";
import parseFileNameFromAxiosResponse from "utils/parseFileNameFromAxiosResponse";
import MonthOfYearRangeRequest from "entities/monthOfYearOpenRangeRequest";
import type InternalReportDetailViewModel from "../internalReportDetailViewModel";
import MonthOptionService from "services/monthOptionService";

const navigationName = "exemption-reports";

@Router.registerRoute({
  name: Router.Self,
  route: navigationName,
})
export default class ExemptionReportsListViewModel extends ListViewModel<
  InternalReportResponse,
  InternalReportDetailViewModel,
  ScreenBase,
  any
> {
  readonly modelName = "internalReportResponse";
  @observable items: InternalReportResponse[] = [];
  @observable fileResponse: any = undefined;
  @observable request = new MonthOfYearRangeRequest();

  readonly idProperty = "id";

  @computed
  get name() {
    return this.translate("name");
  }

  @computed
  get monthlyOptions() {
    return this.monthOptionService.monthOptions;
  }

  @computed
  get monthlyOptionsTo() {
    if (!this.request.from) {
      return this.monthlyOptions;
    }

    // We want to take only the values until the previously selected one.
    const result: any[] = [];
    this.monthlyOptions.some(x => (result.push(x), x.id === this.request.from));

    return result;
  }

  constructor(
    protected detailViewModelFactory: ReturnType<typeof InternalReportDetailViewModel.Factory>,
    public repository: InternalReportsRepository,
    public securityService: SecurityService,
    public eventBus: EventBus,
    public localizationService: LocalizationService,
    public notificationService: NotificationService,
    private monthOptionService: MonthOptionService
  ) {
    super();
    this.navigationName = navigationName;
  }

  protected onActivate(): Promise<void> {
    this.loadData();
    return super.onActivate();
  }

  translate(code: string, size?: number) {
    return this.localizationService.translateModule(Modules.timeCharges, `internalReports.exemptionReports.${code}`, size);
  }

  @action.bound
  @watchBusy
  async onDownload(item: InternalReportResponse) {
    await this.download(item);
  }

  @computed
  get allowToDownload() {
    if (!allowToDownload(this.securityService)) {
      this.notificationService.addNotAllowedError();
      return false;
    }
    return true;
  }

  protected async fetchData() {
    const { limit, offset } = this.pagingFilter;
    const reports = await this.repository.postExemptionsReports({ ...this.appliedFilter, limit, offset });

    reports.items = reports.items.sort((a, b) => {
      if (!a.processedAt || !b.processedAt) {
        return 1;
      }
      return b.processedAt.getTime() - a.processedAt.getTime();
    });

    return reports;
  }

  async download(item: InternalReportResponse) {
    try {
      if (this.allowToDownload) {
        this.fileResponse = await this.repository.downloadReport(item.id);
        download(this.fileResponse.data, parseFileNameFromAxiosResponse(this.fileResponse, item.fileName), "application/zip");
      }
    } catch (error) {
      Sentry.captureException(error);
    }
  }
}
